// an action that needs a one-time SMS code.
import { digitsOnly } from '../rules.ts';
import { $, $$, esc, icon } from './core.ts';
import { mountOtp } from './otp.ts';
import { toast } from './toast.ts';

/** What request() answers: whether a code went out now, seconds until a resend and until it expires, the masked mobile. */
export interface OtpSent {
  sent?: boolean;
  resendIn?: number;
  expiresIn?: number;
  mobile?: string;
  error?: string;
}

export interface OtpStepOptions {
  title?: string;
  length?: number;
  okText?: string;
  /** asks the server for the code; resend = the user pressed «send again» */
  request: (resend: boolean) => Promise<OtpSent | null>;
  /** true when the code was accepted, else the error text */
  verify: (code: string) => Promise<true | string>;
}

// ---------------- T-072 rule: an action that needs a one-time SMS code ----------------
// otpStep({ title, request, verify }) opens the code step: a centred dialog on desktop, a bottom sheet on phones, the page
// dimmed behind it. request(resend) asks the server for the code — the server sends an SMS only for a code not sent yet
// or on an explicit resend after the interval (OneTimeCode.ShouldSend), so closing and reopening never sends twice — and
// answers { sent, resendIn, expiresIn, mobile } (or { error }). verify(code) answers true or an error text. The promise
// resolves true when the code was accepted, false when the step was closed.
export function otpStep({
  title = 'تأیید با کد یک‌بارمصرف',
  length = 6,
  okText = 'تأیید',
  request,
  verify,
}: OtpStepOptions): Promise<boolean> {
  return new Promise(resolve => {
    const id = 'dsotp' + Math.random().toString(36).slice(2, 8);
    const d = document.createElement('dialog');
    d.className = 'ds-dialog ds-otp-sheet';
    d.setAttribute('aria-labelledby', id + '-t');
    d.innerHTML = `<div class="ds-otp-step">
      <div class="head"><span class="mark">${icon('shield')}</span><div><b id="${id}-t">${esc(title)}</b><p class="lead" aria-live="polite">در حال ارسال کد…</p></div>
        <button type="button" class="btn btn-icon" data-x aria-label="بستن">${icon('close')}</button></div>
      <input type="hidden" id="${id}">
      <div class="ds-otp" data-target="${id}" data-length="${length}" role="group" aria-label="کد یک‌بارمصرف"></div>
      <p class="ds-msg text-danger text-center m-0" data-err hidden></p>
      <div class="ds-resend"><span class="t">${icon('clock')}<span data-tm></span></span><button type="button" class="btn btn-sm" data-resend hidden>${icon('reload')}ارسال مجدد کد</button></div>
      <div class="acts"><button type="button" class="btn btn-primary" data-ok disabled>${esc(okText)}</button><button type="button" class="btn btn-ghost" data-x>انصراف</button></div>
    </div>`;
    document.body.appendChild(d);
    const box = $('.ds-otp', d)!,
      code = $<HTMLInputElement>('#' + id, d)!,
      lead = $('.lead', d)!,
      err = $('[data-err]', d)!,
      tm = $('[data-tm]', d)!,
      again = $('[data-resend]', d)!,
      ok = $<HTMLButtonElement>('[data-ok]', d)!;
    mountOtp(box);
    let timer: ReturnType<typeof setInterval> | null = null,
      left = 0,
      life = 0,
      busy = false,
      done = false;
    const finish = (v: boolean): void => {
      if (done) return;
      done = true;
      clearInterval(timer!);
      d.close();
      d.remove();
      resolve(v);
    };
    const tick = (): void => {
      if (life > 0) life--;
      if (left > 0) left--;
      if (life <= 0) {
        tm.textContent = 'کد منقضی شد؛ کد تازه درخواست کنید.';
        again.hidden = false;
        return;
      }
      if (left > 0) {
        tm.textContent = `امکان درخواست مجدد کد تا ${String(Math.floor(left / 60)).padStart(2, '0')}:${String(left % 60).padStart(2, '0')}`;
        again.hidden = true;
      } else {
        tm.textContent = 'کد را دریافت نکرده‌اید؟';
        again.hidden = false;
      }
    };
    const show = (r: OtpSent | null, resent: boolean): void => {
      if (!r || r.error) {
        toast(r?.error || 'ارسال کد ممکن نشد. دوباره تلاش کنید.', { type: 'danger' });
        if (!resent) finish(false);
        return;
      }
      const to = r.mobile ? `<bdi>${esc(r.mobile)}</bdi>` : 'شماره‌ی همراه شما';
      lead.innerHTML = r.sent
        ? `کد ${length} رقمی به ${to} ارسال شد.`
        : `کد ${length} رقمی پیش‌تر به ${to} ارسال شده و هنوز معتبر است.`;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion -- the value can arrive as a string from JSON or a data-* attribute
      left = +r.resendIn! || 0;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion -- the value can arrive as a string from JSON or a data-* attribute
      life = +r.expiresIn! || 0;
      left++;
      life++;
      tick();
      clearInterval(timer!);
      timer = setInterval(tick, 1000);
      if (resent && r.sent) toast('کد تازه ارسال شد.', { type: 'success' });
      $('input', box)?.focus();
    };
    const submit = async (): Promise<void> => {
      const v = digitsOnly(code.value);
      if (busy || v.length !== length) return;
      busy = true;
      ok.disabled = true;
      err.hidden = true;
      let r: true | string;
      try {
        r = await verify(v);
      } catch {
        r = 'بررسی کد ممکن نشد. دوباره تلاش کنید.';
      }
      busy = false;
      if (r === true) {
        finish(true);
        return;
      }
      err.textContent = typeof r === 'string' && r ? r : 'کد واردشده درست نیست.';
      err.hidden = false;
      $$<HTMLInputElement>('input', box).forEach(c => {
        c.value = '';
      });
      code.value = '';
      $('input', box)!.focus();
    };
    code.addEventListener('input', () => {
      ok.disabled = digitsOnly(code.value).length !== length;
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- submit shows its own errors
      if (!ok.disabled) submit();
    });
    ok.addEventListener('click', submit);
    again.addEventListener('click', async () => {
      again.hidden = true;
      let r: OtpSent | null;
      try {
        r = await request(true);
      } catch {
        r = null;
      }
      show(r, true);
    });
    $$('[data-x]', d).forEach(b => b.addEventListener('click', () => finish(false)));
    d.addEventListener('cancel', e => {
      e.preventDefault();
      finish(false);
    });
    d.addEventListener('click', e => {
      if (e.target === d) finish(false);
    });
    d.showModal();
    Promise.resolve()
      .then(() => request(false))
      .then(
        r => show(r, false),
        () => show(null, false),
      );
  });
}
